import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TableLazyLoadEvent } from 'primeng/table';

import { getReport } from '../report-definitions';
import { Division, ReportColumn, ReportDefinition, ReportRow } from '../report.model';
import { ReportDataService } from '../report-data.service';
import { NotificationService } from '../../../core/notification.service';
import { toYearOptions } from '../../../core/reinsurance-years';
import { ReinsuranceYearsService } from '../../../core/reinsurance-years.service';

/**
 * Renders a single Bordereau report: an optional reinsurance-year filter, the
 * data in a PrimeNG table, and an "Export to Excel" action.
 */
@Component({
  selector: 'app-report-viewer',
  standalone: false,
  templateUrl: './report-viewer.component.html',
  styleUrl: './report-viewer.component.scss'
})
export class ReportViewerComponent implements OnInit {
  division!: Division;
  divisionName = '';
  report?: ReportDefinition;

  rows: ReportRow[] = [];
  years: number[] = [];
  selectedYear!: number;

  /** Table paging state; for paged reports this drives the server request. */
  first = 0;
  pageSize = 10;
  totalRecords = 0;

  loading = false;
  exporting = false;
  error: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly data: ReportDataService,
    private readonly reinsuranceYears: ReinsuranceYearsService,
    private readonly title: Title,
    private readonly notifications: NotificationService
  ) {}

  ngOnInit(): void {
    const snapshot = this.route.snapshot;
    this.divisionName = snapshot.data['division'] ?? '';
    this.division = (this.divisionName.toLowerCase() as Division) || 'actuary';
    const key = snapshot.paramMap.get('reportKey') ?? '';
    this.report = getReport(this.division, key);

    if (!this.report) {
      return;
    }

    this.title.setTitle(`${this.report.title} — ${this.divisionName}`);
    if (this.report.requiresYear) {
      this.loadYears();
    } else {
      this.load();
    }
  }

  /**
   * Fills the Reinsurance Year dropdown with the years the report's table
   * holds, selects the latest, then loads that year's data.
   */
  private loadYears(): void {
    if (!this.report) {
      return;
    }
    this.loading = true;
    this.reinsuranceYears.getYears(this.report.key).subscribe({
      next: (years) => this.applyYears(years),
      // Without the year list the report can still load: fall back to recent years.
      error: () => this.applyYears([])
    });
  }

  private applyYears(years: number[]): void {
    this.years = toYearOptions(years);
    this.selectedYear = this.years[0];
    this.load();
  }

  /** Loads the report data for the current selection. */
  load(): void {
    if (!this.report) {
      return;
    }
    this.first = 0;
    if (this.report.paged) {
      this.loadPage();
      return;
    }
    this.loading = true;
    this.error = null;
    const year = this.report.requiresYear ? this.selectedYear : undefined;
    this.data.fetchData(this.report, year).subscribe({
      next: (rows) => {
        this.rows = rows;
        this.totalRecords = rows.length;
        this.loading = false;
      },
      error: () => this.onLoadError()
    });
  }

  /** Table page/page-size change on a server-paged report. */
  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.pageSize = event.rows ?? this.pageSize;
    this.loadPage();
  }

  /** Loads the current page of a server-paged report. */
  private loadPage(): void {
    if (!this.report) {
      return;
    }
    this.loading = true;
    this.error = null;
    const year = this.report.requiresYear ? this.selectedYear : undefined;
    const pageNumber = Math.floor(this.first / this.pageSize) + 1;
    this.data.fetchPage(this.report, pageNumber, this.pageSize, year).subscribe({
      next: (page) => {
        this.rows = page.rows;
        this.totalRecords = page.totalRecords;
        this.loading = false;
      },
      error: () => this.onLoadError()
    });
  }

  private onLoadError(): void {
    this.rows = [];
    this.totalRecords = 0;
    this.loading = false;
    this.error = 'Could not load the report. Please try again.';
  }

  /** Downloads the report as an Excel file. */
  exportExcel(): void {
    if (!this.report) {
      return;
    }
    this.exporting = true;
    this.error = null;
    const year = this.report.requiresYear ? this.selectedYear : undefined;
    const reportTitle = this.report.title;
    this.data.downloadExcel(this.report, year).subscribe({
      next: () => {
        this.exporting = false;
        this.notifications.success('Success', `${reportTitle} exported to Excel.`);
      },
      error: () => {
        this.exporting = false;
        this.error = 'No data available to export for this selection.';
      }
    });
  }

  /** The report's quarter run date (from the first row), formatted. */
  get runDate(): string | null {
    const raw = this.rows[0]?.['qtrrundate'];
    if (raw == null) {
      return null;
    }
    const date = new Date(raw as string);
    return isNaN(date.getTime()) ? null : date.toLocaleDateString('en-US');
  }

  /** Formats a cell value for display according to the column type. */
  formatCell(row: ReportRow, column: ReportColumn): string {
    const value = row[column.field];
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (column.type === 'year') {
      // A calendar year — no thousands separators, no decimals (e.g. 2026, not 2,026.00).
      const n = Number(value);
      return isNaN(n) ? String(value) : String(Math.trunc(n));
    }
    if (column.type === 'number') {
      const n = Number(value);
      return isNaN(n)
        ? String(value)
        : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (column.type === 'date') {
      // Date-only values ("2026-01-01", from C# DateOnly) parse as UTC midnight, which
      // shows as the previous day in US time zones — read them as local dates instead.
      const raw = String(value);
      const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw);
      return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-US');
    }
    return String(value);
  }
}
